/**
 * Soft Proficiency Hook
 * Manages training-based soft proficiency separate from real job-based proficiency.
 * Soft proficiency is capped at 15% per category.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { ConfidenceLevel } from '@/utils/divinationEngine';
// DTC-L: route soft-proficiency lifecycle writes through clock.
import { clock } from '@/utils/clock';

const MAX_SOFT_BOOST = 15.0;

export interface SoftProficiency {
  id: string;
  technician_profile_id: string;
  category_key: string;
  soft_score: number;
  training_completions: number;
  last_training_at: string | null;
}

/**
 * Fetch all soft proficiencies for a technician
 */
export function useSoftProficiencies(technicianProfileId: string | undefined) {
  return useQuery({
    queryKey: ['soft-proficiencies', technicianProfileId],
    queryFn: async () => {
      if (!technicianProfileId) return [];
      
      const { data, error } = await supabase
        .from('technician_soft_proficiencies')
        .select('*')
        .eq('technician_profile_id', technicianProfileId);
      
      if (error) throw error;
      return data as SoftProficiency[];
    },
    enabled: !!technicianProfileId,
  });
}

/**
 * Get soft proficiency for a specific category
 */
export function useSoftProficiencyForCategory(
  technicianProfileId: string | undefined,
  categoryKey: string | undefined
) {
  return useQuery({
    queryKey: ['soft-proficiency', technicianProfileId, categoryKey],
    queryFn: async () => {
      if (!technicianProfileId || !categoryKey) return null;
      
      const { data, error } = await supabase
        .from('technician_soft_proficiencies')
        .select('*')
        .eq('technician_profile_id', technicianProfileId)
        .eq('category_key', categoryKey)
        .maybeSingle();
      
      if (error) throw error;
      return data as SoftProficiency | null;
    },
    enabled: !!technicianProfileId && !!categoryKey,
  });
}

/**
 * Update soft proficiency based on training completion
 * Only gives credit for 'verified' or 'likely' confidence
 */
export function useUpdateSoftProficiency() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({
      technicianProfileId,
      categoryKey,
      completionStatus,
      progressPercent,
      confidence,
    }: {
      technicianProfileId: string;
      categoryKey: string;
      completionStatus: 'completed' | 'partial';
      progressPercent?: number;
      confidence: ConfidenceLevel;
    }) => {
      // No credit for suspicious or unverified completions
      if (confidence === 'suspicious' || confidence === 'unverified') {
        return null;
      }
      
      // Calculate bump based on completion and confidence
      let bump = 0;
      if (completionStatus === 'completed') {
        bump = confidence === 'verified' ? 0.5 : 0.3;
      } else if (progressPercent && progressPercent >= 50) {
        bump = confidence === 'verified' ? 0.2 : 0.1;
      }
      
      if (bump <= 0) return null;
      
      // Get existing soft proficiency
      const { data: existing } = await supabase
        .from('technician_soft_proficiencies')
        .select('soft_score, training_completions')
        .eq('technician_profile_id', technicianProfileId)
        .eq('category_key', categoryKey)
        .maybeSingle();
      
      const currentScore = existing?.soft_score || 0;
      const newScore = Math.min(MAX_SOFT_BOOST, currentScore + bump);
      const newCompletions = (existing?.training_completions || 0) + 1;
      
      // Upsert soft proficiency
      const { error } = await supabase
        .from('technician_soft_proficiencies')
        .upsert({
          technician_profile_id: technicianProfileId,
          category_key: categoryKey,
          soft_score: newScore,
          training_completions: newCompletions,
          last_training_at: clock.now().toISOString(),
          updated_at: clock.now().toISOString(),
        }, {
          onConflict: 'technician_profile_id,category_key',
        });
      
      if (error) throw error;
      
      return { categoryKey, newScore, bump };
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ 
        queryKey: ['soft-proficiencies', variables.technicianProfileId] 
      });
      queryClient.invalidateQueries({ 
        queryKey: ['soft-proficiency', variables.technicianProfileId, variables.categoryKey] 
      });
    },
  });
}

/**
 * Batch update soft proficiencies for multiple categories
 */
export function useBatchUpdateSoftProficiency() {
  const updateMutation = useUpdateSoftProficiency();
  
  return useMutation({
    mutationFn: async ({
      technicianProfileId,
      categoryKeys,
      completionStatus,
      progressPercent,
      confidence,
    }: {
      technicianProfileId: string;
      categoryKeys: string[];
      completionStatus: 'completed' | 'partial';
      progressPercent?: number;
      confidence: ConfidenceLevel;
    }) => {
      const results = await Promise.all(
        categoryKeys.map(categoryKey =>
          updateMutation.mutateAsync({
            technicianProfileId,
            categoryKey,
            completionStatus,
            progressPercent,
            confidence,
          })
        )
      );
      
      return results.filter(Boolean);
    },
  });
}

/**
 * Check nudge eligibility for a technician/category
 */
export function useNudgeEligibility(
  technicianProfileId: string | undefined,
  categoryKey: string | undefined
) {
  return useQuery({
    queryKey: ['nudge-eligibility', technicianProfileId, categoryKey],
    queryFn: async () => {
      if (!technicianProfileId || !categoryKey) {
        return { eligible: false, reason: 'Missing parameters' };
      }
      
      // Get soft proficiency
      const { data: softProf } = await supabase
        .from('technician_soft_proficiencies')
        .select('soft_score')
        .eq('technician_profile_id', technicianProfileId)
        .eq('category_key', categoryKey)
        .maybeSingle();
      
      const softScore = softProf?.soft_score || 0;
      
      if (softScore < 10) {
        return { eligible: false, reason: 'Insufficient soft proficiency (need 10%+)' };
      }
      
      // Get nudge count this month
      const { data: nudgeCount } = await supabase
        .rpc('get_nudge_count_this_month', {
          p_tech_profile_id: technicianProfileId,
          p_category_key: categoryKey,
        });
      
      if ((nudgeCount || 0) >= 3) {
        return { eligible: false, reason: 'Monthly nudge limit reached (3)' };
      }
      
      return { 
        eligible: true, 
        softScore,
        nudgesUsed: nudgeCount || 0,
        nudgesRemaining: 3 - (nudgeCount || 0),
      };
    },
    enabled: !!technicianProfileId && !!categoryKey,
  });
}

/**
 * Log a nudge event
 */
export function useLogNudge() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({
      technicianProfileId,
      categoryKey,
      jobId,
      metadata,
    }: {
      technicianProfileId: string;
      categoryKey: string;
      jobId?: string;
      metadata?: Record<string, unknown>;
    }) => {
      const { error } = await supabase
        .from('technician_soft_proficiency_nudges')
        .insert([{
          technician_profile_id: technicianProfileId,
          category_key: categoryKey,
          job_id: jobId || null,
          metadata: (metadata || {}) as Record<string, string | number | boolean | null>,
        }]);
      
      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ 
        queryKey: ['nudge-eligibility', variables.technicianProfileId, variables.categoryKey] 
      });
    },
  });
}
